/** @format */

import { useContext, useEffect, useReducer, useRef, useState } from "react";

import SupplierView from "../Components/SupplierView";
import {
  SupplierViewPropsTypes,
  ModifiedDataParamsForPut,
  ActionKind,
  InitialStateProps,
} from "../SupplierViewPropsTypes";
import { ApiHandler } from "../../Constants/ApiHandler";
import { NotificationManager } from "react-notifications";
import { useParams, useSearchParams } from "react-router-dom";
import { Notify } from "../../Common/Notify/NotificationMessages";
import {
  DateTypeEnum,
  EnumQuestionType,
  EnumSurveyAnswerStatus,
} from "../../Common/Enums/EnumsTypes";
import images from "../../ImageVariables";
import "../SCSS/styles.css";
import { commonParentCompProps } from "../../commonParentCompPropsTypes";
import ConfirmAlertHome from "../../Common/CommonComponent/ConfirmAlert/Component/ConfirmAlertHome";
import { supplierResponseViewPropsTypes } from "../../SupplierResponseView/SupplierResponseViewPropsTypes";
import { INITIAL_STATE, reducer } from "../reducerFunction";
import { CheckResponsiveContext } from "../../Context/CheckResponsiveContext";
import SupplierMobileView from "../Components/SupplierMobileView";
import { filteredQuestionIds } from "../../Common/CommonFunctions/filterQuestionIds";
import { commonProps } from "../../commonTypes";
import { ConfirmAlerHometTypes } from "../../Common/CommonComponent/ConfirmAlert/ConfirmAlerHometTypes";
import MFA from "../../MFA/Components/Mfa";
import MobileMFA from "../../MFA/Components/MobileMfa";
import Landscape from "../../Landscape";
import { scrollIntoView } from "../../Common/CommonFunctions/ScrollIntoView";
import { toblobWithAuth } from "../../Common/CommonFunctions/toBlobWithAuth";

const monthsHash: SupplierViewPropsTypes["monthsHash"] = {
  0: "Jan",
  1: "Feb",
  2: "March",
  3: "April",
  4: "May",
  5: "June",
  6: "July",
  7: "August",
  8: "September",
  9: "Oct",
  10: "Nov",
  11: "Dec",
};
let currentIndex: number = 0;
function SupplierViewContainer({
  surveyAnswersInfo,
  setSurveyAnswersInfo,
  surveyAnswers,
  setSurveyAnswers,
  questionObjVariable,
  surveyAnswerDocId,
  isSurveyLoaded,
  isPreview,
}: commonParentCompProps) {
  //useReducer state maintained to reduce the states
  const [reducerState, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { isMobile, isLandscape } = useContext(CheckResponsiveContext);
  //one main ref object for to hold all the ref states
  const mainRefObject: SupplierViewPropsTypes["mainRefObject"] = useRef({
    //ref maintained to store questionIds as key with values as unique optionIds
    questionOptionsInfoRef: useRef(new Map()),
    // ref for files to be deleted while clearing or choosing diff options
    filesToDeleteForQuestions: useRef(new Map()),
  });
  //state is maintained for the unique ids of dependency optionIds
  const [dependencyAnsHash, setDependencyAnsHash] = useState<Set<string>>(
    new Set()
  );
  const questionIdRefs = useRef<any>({});
  const { id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const params = searchParams.get("viewSubmission");
  const oldOption = useRef<string>("");
  //fill all question ids , also fill questions ids to render on initial load
  const [mfaDone, setMfaDone] = useState(false);
  //state maintained for get base survey info
  const [getBaseSurveyAnswer, setGetBaseSurveyAnswer] = useState(null);
  const [taggedSurveyNotAnsweredNote, setTaggedSurveyNotAnsweredNote] =
    useState("");
  useEffect(() => {
    let allQuestionIdsDummy: InitialStateProps["allQuestionIds"] = [];
    surveyAnswersInfo?.surveyInfo?.questionsInfo?.questionOrder.forEach(
      (questionId) => {
        allQuestionIdsDummy.push(questionId);
      }
    );
    // filter out questions tht are dependent, and only keep questions to render
    dispatch({
      type: ActionKind.ALL_QUESTION_IDS,
      payload: {
        allQuestionIdsDummy: allQuestionIdsDummy,
      },
    });
    if (!isPreview) {
      const updatedFilterData = filteredQuestionIds(
        allQuestionIdsDummy,
        questionObjVariable,
        dependencyAnsHash
      );
      dispatch({
        type: ActionKind.QUESTION_IDS_TO_RENDER,
        payload: { updatedFilterData: updatedFilterData },
      });
    } else {
      dispatch({
        type: ActionKind.QUESTION_IDS_TO_RENDER,
        payload: {
          updatedFilterData: allQuestionIdsDummy,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencyAnsHash]);
  //get base survey data
  useEffect(() => {
    if (mfaDone && surveyAnswersInfo?.surveyInfo?.baseSurvey) {
      (async () => {
        try {
          const response = await ApiHandler.getBaseSurvey(
            surveyAnswersInfo?.surveyInfo?.baseSurvey._id,
            surveyAnswersInfo.companyId,
            surveyAnswersInfo.supplierId
          );
          if (response.results) {
            setGetBaseSurveyAnswer({ ...response.results });
            if (
              response.results.surveyStatus !== EnumSurveyAnswerStatus.COMPLETED
            ) {
              setTaggedSurveyNotAnsweredNote(
                `Please complete the tagged survey`
              );
            }
          } else {
            setGetBaseSurveyAnswer(null);
          }
        } catch (error) {
          NotificationManager.warning(Notify.DEFAULT, "", 2000);
        }
      })();
    }
  }, [mfaDone, surveyAnswersInfo?.surveyInfo?.baseSurvey]);

  //fill dep ans hash and questionOptionInfoMap with values from survey answers on load. func for dropdown and yes/no.
  function fillHashOnLoadForDropAndYesOrNo(
    questionId: string,
    dependencyAnsHashCopy: Set<string>
  ) {
    dependencyAnsHashCopy.add(surveyAnswers[questionId].optionId!);

    if (mainRefObject.current.questionOptionsInfoRef.current.has(questionId)) {
      mainRefObject.current.questionOptionsInfoRef.current
        .get(questionId)
        ?.selectedoptionsIds.add(surveyAnswers[questionId].optionId!);
    } else {
      mainRefObject.current.questionOptionsInfoRef.current.set(questionId, {
        selectedoptionsIds: new Set([surveyAnswers[questionId].optionId!]),
      });
    }
  }

  //will do better method later
  // if survey answers exists form the dependencyAns hash and questionOptionsInfo hash
  useEffect(() => {
    //get all parent questions under dependency, has atleast 1 dependent children
    let dependencyAnsHashCopy = new Set(dependencyAnsHash);
    for (let questionId in surveyAnswers) {
      if (
        questionObjVariable?.[questionId]?.dependentChildren.length &&
        questionObjVariable?.[questionId]?.questionType !==
          EnumQuestionType.CHECKBOX &&
        surveyAnswers[questionId]["val"]
      ) {
        fillHashOnLoadForDropAndYesOrNo(questionId, dependencyAnsHashCopy);
      } else if (
        questionObjVariable?.[questionId]?.dependentChildren.length &&
        questionObjVariable?.[questionId]?.questionType ===
          EnumQuestionType.CHECKBOX
      ) {
        for (let optId in surveyAnswers[questionId].val) {
          dependencyAnsHashCopy.add(optId);

          if (
            mainRefObject.current.questionOptionsInfoRef.current.has(questionId)
          ) {
            mainRefObject.current.questionOptionsInfoRef.current
              .get(questionId)
              ?.selectedoptionsIds.add(optId);
          } else {
            mainRefObject.current.questionOptionsInfoRef.current.set(
              questionId,
              {
                selectedoptionsIds: new Set([optId]),
              }
            );
          }
        }
      }
    }
    setDependencyAnsHash(dependencyAnsHashCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyAnswersInfo]);

  //function to get file keys of child questions.
  function populateFileKeys(
    questionId: string,
    uploadsArray: {
      name: string;
      path: string;
      Key: string;
      date: Date;
    }[]
  ) {
    for (let element of uploadsArray) {
      if (
        mainRefObject.current.filesToDeleteForQuestions.current.has(questionId)
      ) {
        mainRefObject.current.filesToDeleteForQuestions.current
          .get(questionId)
          ?.fileKeys.push(element.Key);
      } else {
        mainRefObject.current.filesToDeleteForQuestions.current.set(
          questionId,
          {
            fileKeys: [element.Key],
          }
        );
      }
    }
  }

  //function to get file keys of child questions.
  function clearAllFiles(questionId: string) {
    dispatch({
      type: ActionKind.CLEAR_UPLOADED_FILES,
      payload: {
        questionId,
      },
    });
  }
  function clearValuesFromSurveyAnswers(
    questionId: string,
    surveyAnswersCopy?: SupplierViewPropsTypes["surveyAnswers"],
    eventType?: string
  ) {
    surveyAnswersCopy?.[questionId]?.uploads?.length &&
      populateFileKeys(questionId, surveyAnswers[questionId].uploads!);
    if (!eventType) {
      delete surveyAnswersCopy![questionId];
    } else {
      delete surveyAnswersCopy?.[questionId]?.val;
      delete surveyAnswersCopy?.[questionId]?.optionId;
      delete surveyAnswersCopy?.[questionId]?.score;
      delete surveyAnswersCopy?.[questionId]?.uploads;
      delete surveyAnswersCopy?.[questionId]?.isOthersChecked;
      delete surveyAnswersCopy?.[questionId]?.rating;
      delete surveyAnswersCopy?.[questionId]?.emissionFactorConvertedValue;
      delete surveyAnswersCopy?.[questionId]?.selectedOptions;
      delete surveyAnswersCopy?.[questionId]?.others;
      delete surveyAnswersCopy?.[questionId]?.comment;
    }
  }

  function checkToClearFromSurveyAnswers(
    depChildId: string,
    surveyAnswersCopy: SupplierViewPropsTypes["surveyAnswers"],
    dependencyAnsHashCopy: Set<string>
  ) {
    if (
      depChildId in surveyAnswersCopy ||
      reducerState.uploadedFiles.has(depChildId)
    ) {
      let dependentOptsIds = questionObjVariable?.[depChildId]?.dependentOn;
      let vissible = false;
      for (let optId of dependentOptsIds) {
        if (dependencyAnsHashCopy.has(optId)) {
          vissible = true;
          break;
        }
      }

      if (!vissible) {
        clearValuesFromSurveyAnswers(depChildId, surveyAnswersCopy);
        clearAllFiles(depChildId);
      }
    }
  }
  // function for clearing dependency
  function checkNestedDependency(
    dependentChildrenArray: Array<string>,
    surveyAnswersCopy: commonProps["surveyAnswers"],
    dependencyAnsHashCopy: Set<string>
  ) {
    for (let depChildId of dependentChildrenArray) {
      if (!dependentChildrenArray.length) continue;
      checkToClearFromSurveyAnswers(
        depChildId,
        surveyAnswersCopy,
        dependencyAnsHashCopy
      );
      if (
        mainRefObject.current.questionOptionsInfoRef.current.has(depChildId) &&
        !(depChildId in surveyAnswersCopy)
      ) {
        questionObjVariable[depChildId].optionInfo.optionOrder.forEach(
          (ele) => {
            if (dependencyAnsHashCopy.has(ele)) {
              dependencyAnsHashCopy.delete(ele);
              mainRefObject.current.questionOptionsInfoRef.current
                .get(depChildId)
                ?.selectedoptionsIds.delete(ele);
              mainRefObject.current.questionOptionsInfoRef.current.get(
                depChildId
              )?.selectedoptionsIds.size === 0 &&
                mainRefObject.current.questionOptionsInfoRef.current.delete(
                  depChildId
                );
            }
          }
        );
      }

      checkNestedDependency(
        questionObjVariable?.[depChildId]?.dependentChildren,
        surveyAnswersCopy,
        dependencyAnsHashCopy
      );
    }
  }

  //handle clear answers onclick of clearBroom
  const handleClearAnswer: SupplierViewPropsTypes["handleClearAnswer"] = (
    questionId,
    questionType,
    eventType
  ) => {
    // for questions without dependency directly clear and for parent ques with dep
    // delete noteComment?.[questionId];
    // setNoteComment({ ...noteComment });
    let surveyAnswersCopy = structuredClone(surveyAnswers);
    clearValuesFromSurveyAnswers(questionId, surveyAnswersCopy, eventType);
    if (reducerState.uploadedFiles.has(questionId)) {
      clearAllFiles(questionId);
    }
    //if question has no children dont do the below steps
    if (questionObjVariable?.[questionId]?.dependentChildren.length) {
      let dependencyAnsHashCopy = new Set(dependencyAnsHash);
      // dependency part, remove from ansDependencyHash and questionOptionsInfoRef
      switch (questionType) {
        case EnumQuestionType.DROP_DOWN:
        case EnumQuestionType.RADIO:
        case EnumQuestionType.CHECKBOX:
          // when clearing a ques tht is answered , clear from dep ans set  and ques map.  also check if the child dependent to it is
          // answered,if it is clear the childs too.
          if (
            mainRefObject.current.questionOptionsInfoRef.current.has(questionId)
          ) {
            mainRefObject.current.questionOptionsInfoRef.current
              .get(questionId)
              ?.selectedoptionsIds.forEach((optionToBeRemoved) => {
                dependencyAnsHashCopy.delete(optionToBeRemoved);
              });
            mainRefObject.current.questionOptionsInfoRef.current.delete(
              questionId
            );
            checkNestedDependency(
              questionObjVariable?.[questionId]?.dependentChildren,
              surveyAnswersCopy,
              dependencyAnsHashCopy
            );

            const updatedFilterData = filteredQuestionIds(
              reducerState.allQuestionIds,
              questionObjVariable,
              dependencyAnsHashCopy
            );
            dispatch({
              type: ActionKind.QUESTION_IDS_TO_RENDER,
              payload: { updatedFilterData: updatedFilterData },
            });
            setDependencyAnsHash(dependencyAnsHashCopy);
          }
          break;
      }
    }
    if (eventType === "notApplicableEvent") {
      surveyAnswersCopy[questionId] = surveyAnswersCopy[questionId] || {};
      surveyAnswersCopy[questionId]["notApplicableInfo"] =
        surveyAnswersCopy[questionId]["notApplicableInfo"] || {};
      surveyAnswersCopy[questionId]["notApplicableInfo"]["isNotApplicable"] =
        !surveyAnswersCopy[questionId]["notApplicableInfo"]["isNotApplicable"];
      if (
        !surveyAnswersCopy[questionId]["notApplicableInfo"]["isNotApplicable"]
      ) {
        surveyAnswersCopy[questionId]["notApplicableInfo"]["note"] = "";
      }
    }
    setSurveyAnswers(surveyAnswersCopy);
  };
  //handle-change-add-comment where !isPreview is handled for disabling onChange comment
  const handleChangeAddComment: SupplierViewPropsTypes["handleChangeAddComment"] =
    (event) => {
      const { value } = event.target;
      dispatch({
        type: ActionKind.NOTE_ONCHANGE,
        payload: value,
      });
    };
  //handle add note when click ADD inside the modal
  const handleNoteComment = (questionId: string) => {
    setSurveyAnswers(
      (draft: supplierResponseViewPropsTypes["surveyAnswers"]) => {
        draft[reducerState.modalNoteAndAttQuestionId] =
          draft[reducerState.modalNoteAndAttQuestionId] || {};
        draft[reducerState.modalNoteAndAttQuestionId]["comment"] =
          reducerState.noteState[questionId];
      }
    );
  };
  //handle-change-clear-comment where !isPreview is handled for disabling clear btn
  const handleChangeClearComment: SupplierViewPropsTypes["handleChangeClearComment"] =
    (questionId) => {
      setSurveyAnswers(
        (draft: supplierResponseViewPropsTypes["surveyAnswers"]) => {
          if (questionId in draft) draft[questionId].comment = "";
        }
      );
      dispatch({
        type: ActionKind.CLEAR_NOTE,
        payload: questionId,
      });
    };
  // Remove a attachment from add attachment modal
  const removeAttachedFile = (key: string) => {
    dispatch({ type: ActionKind.REMOVE_SELECTED_FILES, payload: key });
  };
  //download attachments
  const downloadAttachmentFile = async (fileKey: string, fileName: string) => {
    ///need to check with rohan n saptham
    await toblobWithAuth(
      `survey-response/attachment/download/${fileKey}`,
      fileName
    );
  };
  //Handle Not-Applicable
  const handleNotApplicableToggle = (
    questionId: string,
    questionType: string
  ) => {
    //clearing the invalid number visually with DOM manipulation
    if (questionType === EnumQuestionType.NUMBER) {
      const data: any = document.getElementById(questionId);
      data!.value = "";
      dispatch({
        type: ActionKind.REMOVE_QUES_ID_FROM_NUM_HASH,
        payload: questionId,
      });
      dispatch({
        type: ActionKind.SET_VALID_NUMBER_ERROR_MSG,
        payload: {
          questionId: questionId,
          errorMessage: "",
        },
      });
    }
    handleClearAnswer(questionId, questionType, "notApplicableEvent");
  };
  //Handle Not-Applicable note
  const handleNotApplicableNote = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    questionId: string
  ) => {
    const { value } = e.target;
    setSurveyAnswers((draft: SupplierViewPropsTypes["surveyAnswers"]) => {
      draft[questionId]["notApplicableInfo"]["note"] = value;
    });
  };

  const formData = useRef<FormData>(new FormData());
  //handle confirm delete files
  const confirmDeleteFile: SupplierViewPropsTypes["confirmDeleteFile"] = (
    key,
    questionId
  ) => {
    const confirmParameters: ConfirmAlerHometTypes["confirmParameters"] = {
      title: {
        images: images.deleteAlert,
        titleName: "Delete Confirmation",
      },
      descriptions: {
        first: "Are you sure you want to delete this uploaded file?",
        second: "",
        third: "",
      },
      note: "",
      buttons: {
        Yes: "Delete",
        No: "Cancel",
      },
      onClick: () => {
        handleDeleteFile!(key, questionId!);
      },
      buttonClassName: {
        yes: "button-delete-yes",
        no: "button-delete-no",
      },
      enableOnClose: true,
      enableDescription: true,
      isMobile: isMobile,
    };
    return ConfirmAlertHome({ confirmParameters });
  };

  // handle delete file
  const handleDeleteFile: SupplierViewPropsTypes["handleDeleteFile"] = async (
    key,
    questionId
  ) => {
    try {
      const deleteParams = {
        docId: id,
        questionId: questionId,
        key: key,
      };
      const response = await ApiHandler.deleteUploadedFile(deleteParams);

      setSurveyAnswers((draft: SupplierViewPropsTypes["surveyAnswers"]) => {
        draft[questionId] = draft[questionId] || {};
        draft[questionId]["uploads"] =
          response.results.surveyAnswers[questionId]?.uploads || [];
      });
      NotificationManager.success(Notify.FILE_DELETED, "", 2000);
    } catch (error) {
      NotificationManager.warning(Notify.DEFAULT, "", 2000);
    }
  };

  //put-answers where !isPreview is handled for disabling saveAsDraft btn
  const SaveAsDraft = async () => {
    let modifiedData: ModifiedDataParamsForPut = {
      surveyAnswers: surveyAnswers,
      status: EnumSurveyAnswerStatus?.SAVED_AS_DRAFT,
    };
    try {
      const response = await ApiHandler.putAnswer({
        surveyAnswerDocId,
        modifiedData,
      });

      setSurveyAnswersInfo!({
        ...surveyAnswersInfo,
        surveyInfo: {
          ...response.results.surveyInfo,
        },
        surveyStatus: response.results.surveyStatus,
      });
      if (reducerState.uploadedFiles.size) {
        formData.current.append("docId", id);
        for (let [questionId, files] of reducerState.uploadedFiles) {
          files.selectedFiles.forEach((file) => {
            formData.current.append(questionId, file);
          });
        }
        const fileResponse = await ApiHandler.postFiles(formData.current);
        setSurveyAnswers({ ...fileResponse.results.surveyAnswers });
        //create new map for the uploaded files
        dispatch({
          type: ActionKind.CREATE_NEW_UPLOADED_FILE_MAP,
        });
        formData.current = new FormData();
      }
      if (mainRefObject.current.filesToDeleteForQuestions.current.size) {
        const deleteParams = {
          deleteData: Object.fromEntries(
            mainRefObject.current.filesToDeleteForQuestions.current
          ),
        };
        await ApiHandler.deleteMultipleUploadedFiles(deleteParams);
        //clear the filesToDeleteForQuestions
        mainRefObject.current.filesToDeleteForQuestions.current = new Map();
      }
      !isMobile &&
        NotificationManager.success(Notify?.SAVED_AS_DRAFT, "", 2000);
    } catch (error) {
      NotificationManager.warning(Notify?.DEFAULT, "", 2000);
    }
  };

  //handle confirm submit
  const confirmSubmit = () => {
    const confirmParameters = {
      title: {
        images: images.deleteAlert,
        titleName: "Alert!",
      },
      descriptions: {
        first: Notify.SUBMIT_MSG(surveyAnswersInfo?.companyInfo?.scmName),
        second: "",
        third: "",
      },
      note: "",
      buttons: {
        Yes: "Yes",
        No: "Cancel",
      },
      onClick: () => {
        handleSubmitAnswers();
      },
      buttonClassName: {
        yes: "button-confirm-ok",
        no: "button-confirm-cancel",
      },
      enableOnClose: true,
      enableDescription: true,
      isMobile: false,
    };
    return ConfirmAlertHome({ confirmParameters });
  };

  // handle submit
  const handleSubmitAnswers = async () => {
    if (!isMobile) {
      for (const [
        index,
        quesId,
      ] of reducerState.questionIdsToRender.entries()) {
        switch (questionObjVariable[quesId].questionType) {
          case EnumQuestionType.CHECKBOX:
            if (
              (!surveyAnswers[quesId]?.notApplicableInfo?.isNotApplicable &&
                questionObjVariable[quesId].validation.mandatory.isMandatory &&
                !surveyAnswers[quesId]?.val &&
                surveyAnswers[quesId]?.val !== 0) ||
              (!surveyAnswers[quesId]?.notApplicableInfo?.isNotApplicable &&
                questionObjVariable[quesId].validation.mandatory.isMandatory &&
                surveyAnswers[quesId]?.isOthersChecked &&
                !surveyAnswers[quesId].val[
                  questionObjVariable?.[quesId]?.optionInfo?.optionObj[
                    questionObjVariable?.[quesId]?.optionInfo?.optionOrder[
                      questionObjVariable?.[quesId]?.optionInfo?.optionOrder
                        .length - 1
                    ]
                  ].optionId
                ].othersAnswer)
            ) {
              scrollIntoView(questionIdRefs, quesId);
              NotificationManager.warning(
                Notify?.MANDATORY_QSTN_WARN(index + 1),
                "",
                2000
              );
              return;
            }
            break;
          case EnumQuestionType.DATE:
            if (
              !surveyAnswers[quesId]?.notApplicableInfo?.isNotApplicable &&
              questionObjVariable[quesId].validation.mandatory.isMandatory &&
              !surveyAnswers[quesId]?.val
            ) {
              scrollIntoView(questionIdRefs, quesId);
              NotificationManager.warning(
                Notify?.MANDATORY_QSTN_WARN(index + 1),
                "",
                2000
              );
              return;
            } else if (
              questionObjVariable[quesId].dateInfo!.dateType ===
                DateTypeEnum.RANGE &&
              surveyAnswers[quesId]?.val.from.toString().length &&
              !surveyAnswers[quesId]?.val.to.toString().length
            ) {
              scrollIntoView(questionIdRefs, quesId);
              NotificationManager.warning(
                Notify?.MANDATORY_QSTN_DATE_TO_WARN(index + 1),
                "",
                2000
              );
              return;
            }
            break;
          case EnumQuestionType.FILE:
            if (
              !surveyAnswers[quesId]?.notApplicableInfo?.isNotApplicable &&
              questionObjVariable[quesId].validation.mandatory.isMandatory &&
              !reducerState.uploadedFiles.get(quesId)?.selectedFiles.length &&
              !surveyAnswers?.[quesId]?.uploads?.length
            ) {
              scrollIntoView(questionIdRefs, quesId);
              NotificationManager.warning(
                Notify?.MANDATORY_QSTN_WARN(index + 1),
                "",
                2000
              );
              return;
            }
            break;
          default:
            if (
              !surveyAnswers[quesId]?.notApplicableInfo?.isNotApplicable &&
              questionObjVariable[quesId].validation.mandatory.isMandatory &&
              !surveyAnswers[quesId]?.val &&
              surveyAnswers[quesId]?.val !== 0
            ) {
              scrollIntoView(questionIdRefs, quesId);
              NotificationManager.warning(
                Notify?.MANDATORY_QSTN_WARN(index + 1),
                "",
                2000
              );
              return;
            }
        }
      }
    }
    //variable to keep ids of file upload ques tht have files currently
    let fileUploadQuesIdsWithFiles: Array<string> = [];
    reducerState.uploadedFiles.forEach((val, quesId) => {
      if (questionObjVariable[quesId].questionType === EnumQuestionType.FILE) {
        fileUploadQuesIdsWithFiles.push(quesId);
      }
    });

    let modifiedData: ModifiedDataParamsForPut = {
      surveyAnswers: surveyAnswers,
      status: EnumSurveyAnswerStatus?.COMPLETED,
      questionIds: reducerState.questionIdsToRender,
      fileUploadQuesIdsWithFiles: fileUploadQuesIdsWithFiles,
    };
    let fileResponse: any;
    try {
      dispatch({
        type: ActionKind?.API_LOADING,
        payload: true,
      });
      const response = await ApiHandler.putAnswer({
        surveyAnswerDocId,
        modifiedData,
      });

      if (isMobile) {
        sessionStorage.removeItem("currentQuestion");
        sessionStorage.removeItem("currentPage");
      }

      if (reducerState.uploadedFiles.size) {
        formData.current.append("docId", id);
        for (let [questionId, files] of reducerState.uploadedFiles) {
          files.selectedFiles.forEach((file) => {
            formData.current.append(questionId, file);
          });
        }

        fileResponse = await ApiHandler.postFiles(formData.current);

        //create new map for the uploaded files
        dispatch({
          type: ActionKind.CREATE_NEW_UPLOADED_FILE_MAP,
        });
        formData.current = new FormData();
      }
      if (mainRefObject.current.filesToDeleteForQuestions.current.size) {
        const deleteParams = {
          deleteData: Object.fromEntries(
            mainRefObject.current.filesToDeleteForQuestions.current
          ),
        };
        await ApiHandler.deleteMultipleUploadedFiles(deleteParams);
        //clear the filesToDeleteForQuestions
        mainRefObject.current.filesToDeleteForQuestions.current = new Map();
      }
      setSurveyAnswersInfo!({
        ...surveyAnswersInfo,

        surveyInfo: {
          ...response.results.surveyInfo,
        },
        surveyStatus: response.results.surveyStatus,
        surveyScoreInfo: {
          impact: response.results.surveyScoreInfo.impact,
          tittle: response.results.surveyScoreInfo.tittle,
          score: response.results.surveyScoreInfo.score,
          totalMaxWeightage: response.results.surveyScoreInfo.totalMaxWeightage,
        },
      });
      if (fileResponse) {
        setSurveyAnswers((draft: any) => {
          for (const element in fileResponse.results.surveyAnswers) {
            draft[element] = draft[element] || {};
            draft[element].uploads =
              fileResponse.results.surveyAnswers[element].uploads;
          }
        });
      }
      NotificationManager.success(Notify?.SUBMITTED_SUCCESSFULLY, "", 2000);
      dispatch({
        type: ActionKind?.API_LOADING,
        payload: false,
      });
    } catch (error) {
      NotificationManager.warning(Notify?.DEFAULT, "", 2000);
    }
  };
  // mobile responsive states
  let [currentPage, setCurrentPage] = useState<number>(0);
  const handleNextPage = (assessmentStart?: boolean) => {
    setCurrentPage(currentPage + 1);
    if (assessmentStart) {
      sessionStorage.setItem("currentQuestion", "0");
      sessionStorage.setItem("currentPage", "3");
    }
  };

  const mobileViewPages = [
    {
      components: "MobileViewCompanyInfo",
    },
    {
      components: "MobileViewSupplierInfo",
    },
    {
      components: "NoteAndGuidance",
    },
    { components: "QuestionsMobileView" },
  ];
  // on refresh of page get current page details from browser storage if it exists
  useEffect(() => {
    if (isMobile) {
      let currentPageFromStorage = sessionStorage.getItem("currentPage");
      currentPageFromStorage && setCurrentPage(Number(currentPageFromStorage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);
  const [mfaLoaded, setMfaLoaded] = useState(false);
  // for generating the mfa code for suppliers who click on the link
  useEffect(() => {
    const mfaCodeGeneraton = async () => {
      try {
        const sessionStorageValue = sessionStorage.getItem(
          `${surveyAnswersInfo.supplierInfo?.contactDetails?.emailId}+${id}+mfaVerified`
        );
        if (sessionStorageValue !== "verified") {
          await ApiHandler.supplierMfaEmail(id);
        } else {
          setMfaDone(true);
        }
        setMfaLoaded(true);
      } catch (error) {
        console.error(error);
      }
    };
    if (id && surveyAnswersInfo.supplierInfo?.contactDetails?.emailId) {
      mfaCodeGeneraton();
    }
  }, [id, surveyAnswersInfo.supplierInfo?.contactDetails?.emailId]);

  //MFA handlings
  //state maintained for creating the input filed of MFA files
  const [otp, setOtp] = useState(new Array(6).fill(""));
  //ref to focusOn the input fields
  const inputRef = useRef<HTMLInputElement>(null);
  //state maintained for checking the input field is focused on correct index
  const [activeOTPIndex, setActiveOTPIndex] = useState<number>(0);
  //state maintained to check empty input field
  const [otpError, setOtpError] = useState("");

  //handle onPaste OTP code in MFA
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    let otpArray = pasteData.trim().split("").slice(0, otp.length);
    if (otpArray.length < 6) {
      otpArray = [...otpArray, ...new Array(6 - otpArray.length).fill("")];
    }
    const newOtp = [...otpArray];
    setOtp(newOtp);
    setActiveOTPIndex(newOtp.length - 1);
  };
  //handle OTP change in MFA
  const handelOtpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const newOtp: string[] = [...otp];
    newOtp[currentIndex] = value.substring(value.length - 1);
    if (!value) setActiveOTPIndex(currentIndex - 1);
    else setActiveOTPIndex(currentIndex + 1);
    setOtp(newOtp);
    setOtpError("");
  };
  //useEffect for keeping the focused input field for currentIndex
  useEffect(() => {
    inputRef.current?.focus();
  }, [activeOTPIndex]);
  //handle downKey press when current index assigned with index to focus on previous input field
  const handleOnkeyDown = (
    { key }: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    currentIndex = index;
    if (key === "Backspace" && otp[index] === "") setActiveOTPIndex(index - 1);
    if (key === "Enter") handleVerifyOTP();
  };
  //api integration for MFA
  const handleVerifyOTP = async () => {
    const isOtpEmpty = otp.some((value) => value === "");
    if (!isOtpEmpty) {
      //convert array of string values of OTP to number before sending to BE
      const combinedNumber = parseInt(otp.join(""), 10);
      let modifiedData = {
        param: {
          documentId: id,
          code: combinedNumber,
        },
      };
      try {
        const response = await ApiHandler.postOTP(modifiedData);
        if (response.results === "success") {
          sessionStorage.setItem(
            `${surveyAnswersInfo.supplierInfo.contactDetails.emailId}+${id}+mfaVerified`,
            `verified`
          );
          setMfaDone(true);
        }
        NotificationManager.success(Notify.VERIFIED_OTP, "", 2000);
      } catch (error: any) {
        NotificationManager.warning(
          `${error?.response?.data?.error?.error}`,
          "",
          2000
        );
      }
    } else {
      setOtpError(
        "Oops! looks like you have entered wrong OTP, please try again"
      );
      setOtp(new Array(6).fill("")); // Empty all the input fields
      setActiveOTPIndex(0); // Reset the active OTP index
    }
  };
  //handle resend OTP
  const handleResendOTP = async () => {
    try {
      let modifiedData = {
        documentId: id,
      };
      await ApiHandler.postResendOTP(modifiedData);
      setOtp(new Array(6).fill("")); // Empty all the input fields
      setActiveOTPIndex(0); // Reset the active OTP index
      setOtpError("");
      NotificationManager.success(Notify.RESEND_OTP, "", 2000);
    } catch (error: any) {
      NotificationManager.warning(
        `${error?.response?.data?.error?.error}`,
        "",
        2000
      );
    }
  };
  //state maintained to view past repsonse
  const [isPastResponseView, setIsPastResponseView] = useState(false);
  //handle change for view response toggle
  const handlePastResponseViewChange = () => {
    if (surveyAnswersInfo?.surveyInfo?.baseSurvey) {
      setIsPastResponseView(!isPastResponseView);
    }
  };
  //state maintained for hovering of labels
  const [isHoveredLabel, setIsHoveredLabel] = useState({});

  const [questionIndexMap, setQuestionIndexMap] = useState({});

  const generateQuestionIndex = (surveyData: any) => {
    const indexMap: { [key: string]: string } = {};
    const questionOrder =
      surveyAnswersInfo?.surveyInfo?.questionsInfo?.questionOrder;
    const questionObj =
      surveyAnswersInfo?.surveyInfo?.questionsInfo?.questionObj;
    let topLevelCounter = 1;
    const graph: { [key: string]: string[] } = {};
    const inDegree: { [key: string]: number } = {};

    const queue: string[] = [];
    for (const questionId of questionOrder) {
      const parentId = questionObj[questionId]?.dependentOnQuestionIds?.[0];
      inDegree[questionId] = parentId ? 1 : 0;
      if (parentId) {
        if (!graph[parentId]) graph[parentId] = [];
        graph[parentId].push(questionId);
      } else {
        queue.push(questionId);
      }
    }

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const parentId = questionObj[currentId].dependentOnQuestionIds?.[0];
      if (!parentId) {
        indexMap[currentId] = String(topLevelCounter++);
      } else {
        const parentIndex = indexMap[parentId];
        const siblings = graph[parentId];
        const position = siblings.indexOf(currentId) + 1;
        indexMap[currentId] = `${parentIndex}.${position}`;
      }
      if (graph[currentId]) {
        for (const childId of graph[currentId]) {
          inDegree[childId]--;
          if (inDegree[childId] === 0) queue.push(childId);
        }
      }
    }
    return indexMap;
  };

  useEffect(() => {
    if (surveyAnswersInfo?.surveyInfo?.questionsInfo?.questionOrder.length) {
      const indexMap = generateQuestionIndex(surveyAnswersInfo);
      setQuestionIndexMap(indexMap);
    }
  }, [surveyAnswersInfo?.surveyInfo?.questionsInfo?.questionOrder]);
  return (
    <>
      {(isPreview || mfaLoaded) && (
        <>
          {mfaDone || isPreview ? (
            <>
              {!isMobile ? (
                <SupplierView
                  surveyAnswers={surveyAnswers}
                  handleChangeAddComment={handleChangeAddComment}
                  handleChangeClearComment={handleChangeClearComment}
                  surveyAnswersInfo={surveyAnswersInfo}
                  SaveAsDraft={SaveAsDraft}
                  handleSubmitAnswers={handleSubmitAnswers}
                  questionObjVariable={questionObjVariable}
                  isSurveyLoaded={isSurveyLoaded}
                  isPreview={isPreview}
                  monthsHash={monthsHash}
                  confirmSubmit={confirmSubmit}
                  confirmDeleteFile={confirmDeleteFile}
                  params={params}
                  handleClearAnswer={handleClearAnswer}
                  mainRefObject={mainRefObject}
                  setSurveyAnswers={setSurveyAnswers}
                  clearValuesFromSurveyAnswers={clearValuesFromSurveyAnswers}
                  checkNestedDependency={checkNestedDependency}
                  clearAllFiles={clearAllFiles}
                  oldOption={oldOption}
                  dependencyAnsHash={dependencyAnsHash}
                  setDependencyAnsHash={setDependencyAnsHash}
                  dispatch={dispatch}
                  reducerState={reducerState}
                  questionIdRefs={questionIdRefs}
                  handleNoteComment={handleNoteComment}
                  removeAttachedFile={removeAttachedFile}
                  downloadAttachmentFile={downloadAttachmentFile}
                  getBaseSurveyAnswer={getBaseSurveyAnswer}
                  handlePastResponseViewChange={handlePastResponseViewChange}
                  isPastResponseView={isPastResponseView}
                  taggedSurveyNotAnsweredNote={taggedSurveyNotAnsweredNote}
                  handleNotApplicableToggle={handleNotApplicableToggle}
                  handleNotApplicableNote={handleNotApplicableNote}
                  setIsHoveredLabel={setIsHoveredLabel}
                  isHoveredLabel={isHoveredLabel}
                  questionIndexMap={questionIndexMap}
                />
              ) : !isLandscape ? (
                <SupplierMobileView
                  currentPage={currentPage}
                  handleNextPage={handleNextPage}
                  mobileViewPages={mobileViewPages}
                  surveyAnswersInfo={surveyAnswersInfo}
                  monthsHash={monthsHash}
                  surveyAnswers={surveyAnswers}
                  setSurveyAnswers={setSurveyAnswers}
                  questionObjVariable={questionObjVariable}
                  isPreview={isPreview}
                  setCurrentPage={setCurrentPage}
                  reducerState={reducerState}
                  oldOption={oldOption}
                  mainRefObject={mainRefObject}
                  checkNestedDependency={checkNestedDependency}
                  dependencyAnsHash={dependencyAnsHash}
                  setDependencyAnsHash={setDependencyAnsHash}
                  dispatch={dispatch}
                  handleChangeAddComment={handleChangeAddComment}
                  confirmDeleteFile={confirmDeleteFile}
                  handleClearAnswer={handleClearAnswer}
                  clearValuesFromSurveyAnswers={clearValuesFromSurveyAnswers}
                  SaveAsDraft={SaveAsDraft}
                  handleSubmitAnswers={handleSubmitAnswers}
                  params={params}
                  removeAttachedFile={removeAttachedFile}
                  handleNoteComment={handleNoteComment}
                />
              ) : (
                <Landscape />
              )}
            </>
          ) : (
            <>
              {!isMobile ? (
                <MFA
                  emailId={
                    surveyAnswersInfo.supplierInfo?.contactDetails.emailId
                  }
                  otp={otp}
                  handelOtpChange={handelOtpChange}
                  inputRef={inputRef}
                  activeOTPIndex={activeOTPIndex}
                  handleOnkeyDown={handleOnkeyDown}
                  handleVerifyOTP={handleVerifyOTP}
                  handleResendOTP={handleResendOTP}
                  otpError={otpError}
                  handleOtpPaste={handleOtpPaste}
                  assessmentName={surveyAnswersInfo?.surveyInfo?.surveyName}
                />
              ) : (
                <MobileMFA
                  emailId={
                    surveyAnswersInfo.supplierInfo?.contactDetails.emailId
                  }
                  assessmentName={surveyAnswersInfo?.surveyInfo?.surveyName}
                  otp={otp}
                  handelOtpChange={handelOtpChange}
                  inputRef={inputRef}
                  activeOTPIndex={activeOTPIndex}
                  handleOnkeyDown={handleOnkeyDown}
                  handleVerifyOTP={handleVerifyOTP}
                  handleResendOTP={handleResendOTP}
                  otpError={otpError}
                  handleOtpPaste={handleOtpPaste}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
export default SupplierViewContainer;
